import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { usePublishToEmma } from '../hooks/usePublishToEmma';
import portalT from '../translations/portal';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import {
  ArrowLeft, TrendingUp, Upload, Send, Loader, Lightbulb, Mail,
  FileSpreadsheet, MessageSquare, Plus, Users, Target, Zap,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

const SalesAssistant = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const { lang } = useLanguage();
  const t = portalT[lang];
  const tp = t.toolPages.sales;
  const fileRef = useRef(null);
  const chatEndRef = useRef(null);

  const [view, setView] = useState('start');
  const [messages, setMessages] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [input, setInput] = useState('');
  const [leadsText, setLeadsText] = useState('');
  const [emailCtx, setEmailCtx] = useState('');

  // Publish last assistant message to Emma
  const lastAssistant = (Array.isArray(messages) ? messages : []).filter(m => m.role === 'assistant').slice(-1)[0]?.content;
  usePublishToEmma('AI Sales Assistant', lastAssistant);
  const [emailLang, setEmailLang] = useState('en');
  const [activeTab, setActiveTab] = useState('analyze');

  const headers = { Authorization: `Bearer ${token}` };

  const uploadLeads = async (file) => {
    if (!file) return;
    setLoading(true); setView('chat');
    const fd = new FormData(); fd.append('file', file);
    try {
      const res = await axios.post(`${API}/compass/sales/upload-leads?language=${lang}`, fd, { headers: { ...headers, 'Content-Type': 'multipart/form-data' } });
      setSessionId(res.data.session_id);
      setMessages([{ role: 'user', content: `Uploaded ${file.name} (${res.data.rows} leads)` }, { role: 'assistant', content: res.data.analysis }]);
    } catch (e) { setMessages([{ role: 'assistant', content: (lang === 'en' ? 'Upload failed: ' : 'Upload mislukt: ') + (e.response?.data?.detail || e.message) }]); }
    finally { setLoading(false); }
  };

  const analyzeText = async () => {
    if (!((leadsText) == null ? '' : String(leadsText)).trim()) return;
    setLoading(true); setView('chat');
    try {
      const res = await axios.post(`${API}/compass/sales/analyze-leads`, { leads_text: leadsText, language: lang }, { headers });
      setSessionId(res.data.session_id);
      setMessages([{ role: 'user', content: leadsText }, { role: 'assistant', content: res.data.analysis }]);
    } catch (e) { setMessages([{ role: 'assistant', content: lang === 'en' ? 'Analysis failed. Please check your API configuration.' : 'Analyse mislukt. Controleer je API-configuratie.' }]); }
    finally { setLoading(false); }
  };

  const draftEmail = async () => {
    if (!((emailCtx) == null ? '' : String(emailCtx)).trim()) return;
    setLoading(true); setView('chat');
    try {
      const res = await axios.post(`${API}/compass/sales/draft-email`, { context: emailCtx, language: emailLang, session_id: sessionId }, { headers });
      setSessionId(res.data.session_id);
      setMessages(prev => [...prev, { role: 'user', content: `Draft email: ${emailCtx}` }, { role: 'assistant', content: res.data.draft }]);
      setEmailCtx('');
    } catch (e) { setMessages(prev => [...prev, { role: 'assistant', content: lang === 'en' ? 'Drafting failed. Please try again.' : 'Opstellen mislukt. Probeer het opnieuw.' }]); }
    finally { setLoading(false); }
  };

  const sendChat = async (text) => {
    if (!((text) == null ? '' : String(text)).trim() || loading || !sessionId) return;
    setMessages(prev => [...prev, { role: 'user', content: text }]);
    setInput(''); setLoading(true);
    try {
      const res = await axios.post(`${API}/compass/sales/chat`, { message: text, session_id: sessionId, language: lang }, { headers });
      setMessages(prev => [...prev, { role: 'assistant', content: res.data.response }]);
    } catch (e) { setMessages(prev => [...prev, { role: 'assistant', content: lang === 'en' ? 'Error. Please try again.' : 'Fout. Probeer het opnieuw.' }]); }
    finally { setLoading(false); }
  };

  const startNew = () => { setView('start'); setMessages([]); setSessionId(null); setLeadsText(''); };

  if (view === 'start') {
    return (
      <div className="tool-page">
        <div className="tool-header">
          <button onClick={() => navigate('/portal/ai-tools')} className="btn-secondary"><ArrowLeft size={16} /> {t.common.aiTools}</button>
          <h1 className="heading-1"><TrendingUp size={22} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 8 }} /> {tp.title}</h1>
        </div>
        <div className="tool-content">
          <div className="auto-start-section" data-testid="sales-start">
            {/* Tab switcher */}
            <div className="compass-sidebar-nav" style={{ flexDirection: 'row', justifyContent: 'center', gap: '0.5rem', marginBottom: '1.5rem' }}>
              <button className={`compass-nav-item ${activeTab === 'analyze' ? 'active' : ''}`} onClick={() => setActiveTab('analyze')} data-testid="tab-analyze">
                <Target size={16} /> {lang === 'nl' ? 'Analyseer Leads' : 'Analyze Leads'}
              </button>
              <button className={`compass-nav-item ${activeTab === 'email' ? 'active' : ''}`} onClick={() => setActiveTab('email')} data-testid="tab-email">
                <Mail size={16} /> {lang === 'nl' ? 'Schrijf E-mail' : 'Draft Email'}
              </button>
            </div>

            {activeTab === 'analyze' && (
              <div className="auto-input-card" data-testid="analyze-card">
                <div className="auto-input-header"><Users size={20} /><h3 className="heading-3">{lang === 'nl' ? 'Analyseer je Leads' : 'Analyze Your Leads'}</h3></div>
                <p className="body-small text-secondary">{lang === 'nl' ? 'Upload CRM/lead data of plak lead info voor AI scoring en aanbevelingen.' : 'Upload CRM/lead data or paste lead info for AI scoring and recommendations.'}</p>
                <div className="explorer-dropzone" style={{ margin: '1rem 0', padding: '2rem' }} onClick={() => fileRef.current?.click()} data-testid="leads-dropzone">
                  <Upload size={28} />
                  <span className="body-small">{tp.uploadLeads}</span>
                  <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls" style={{ display: 'none' }} onChange={(e) => uploadLeads(e.target.files[0])} />
                </div>
                <div style={{ textAlign: 'center', margin: '0.5rem 0', color: 'var(--text-secondary)' }}>{tp.orDescribe}</div>
                <textarea className="form-input auto-textarea" placeholder={tp.analyzePlaceholder} value={leadsText} onChange={(e) => setLeadsText(e.target.value)} rows={4} data-testid="leads-textarea" />
                <button className="btn-primary" style={{ width: '100%', marginTop: '1rem' }} onClick={analyzeText} disabled={!((leadsText) == null ? '' : String(leadsText)).trim()} data-testid="analyze-btn">
                  <Zap size={16} /> {lang === 'nl' ? 'Score & Analyseer Leads' : 'Score & Analyze Leads'}
                </button>
              </div>
            )}

            {activeTab === 'email' && (
              <div className="auto-input-card" data-testid="email-card">
                <div className="auto-input-header"><Mail size={20} /><h3 className="heading-3">{lang === 'nl' ? 'Schrijf Verkoop E-mail' : 'Draft Sales Email'}</h3></div>
                <p className="body-small text-secondary">{lang === 'nl' ? 'Beschrijf de situatie en krijg een verzendklare follow-up e-mail.' : 'Describe the situation and get a ready-to-send follow-up email.'}</p>
                <textarea className="form-input auto-textarea" placeholder={lang === 'nl' ? 'Bijv. Follow up met Jan van ABC Corp die interesse toonde in ons premium plan tijdens de demo vorige week...' : 'E.g., Follow up with John from ABC Corp who showed interest in our premium plan during last week\'s demo...'} value={emailCtx} onChange={(e) => setEmailCtx(e.target.value)} rows={4} data-testid="email-textarea" />
                <div style={{ display: 'flex', gap: '0.5rem', margin: '1rem 0' }}>
                  <button className={`compass-nav-item ${emailLang === 'en' ? 'active' : ''}`} onClick={() => setEmailLang('en')} style={{ flex: 1, justifyContent: 'center' }}>English</button>
                  <button className={`compass-nav-item ${emailLang === 'nl' ? 'active' : ''}`} onClick={() => setEmailLang('nl')} style={{ flex: 1, justifyContent: 'center' }}>Nederlands</button>
                </div>
                <button className="btn-primary" style={{ width: '100%' }} onClick={draftEmail} disabled={!((emailCtx) == null ? '' : String(emailCtx)).trim()} data-testid="draft-btn">
                  <Mail size={16} /> {lang === 'nl' ? 'Genereer E-mail Concept' : 'Generate Email Draft'}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="compass-coach-page" data-testid="sales-workspace">
      <aside className="compass-sidebar">
        <button onClick={() => navigate('/portal/ai-tools')} className="btn-secondary" style={{ width: '100%', justifyContent: 'center' }}><ArrowLeft size={14} /> {t.common.aiTools}</button>
        <div className="compass-role-badge"><TrendingUp size={16} /><span className="body-small" style={{ fontWeight: 500 }}>{tp.title}</span></div>
        <div className="auto-sidebar-actions">
          <button className="auto-action-btn" onClick={() => sendChat(lang === 'nl' ? 'Score alle leads en rangschik op conversiekans' : 'Score all leads and rank by conversion probability')} disabled={loading}><Target size={14} /> {lang === 'nl' ? 'Herscore Leads' : 'Re-Score Leads'}</button>
          <button className="auto-action-btn" onClick={() => sendChat(lang === 'nl' ? 'Genereer follow-up e-mail templates voor de top 3 leads' : 'Generate follow-up email templates for the top 3 leads')} disabled={loading}><Mail size={14} /> {lang === 'nl' ? 'Schrijf Follow-ups' : 'Draft Follow-ups'}</button>
          <button className="auto-action-btn" onClick={() => sendChat(lang === 'nl' ? 'Vat de pipeline gezondheid samen en stel prioriteitsacties voor' : 'Summarize pipeline health and suggest priority actions')} disabled={loading}><Lightbulb size={14} /> {lang === 'nl' ? 'Pipeline Samenvatting' : 'Pipeline Summary'}</button>
        </div>
        <button className="compass-new-chat" onClick={startNew}><Plus size={14} /> {t.common.newAnalysis}</button>
      </aside>
      <main className="compass-main">
        <div className="compass-chat-container">
          <div className="compass-chat-messages" data-testid="sales-messages">
            {loading && messages.length === 0 && (
              <div className="auto-analyzing-state"><Loader className="spin" size={32} /><h3 className="heading-3">{lang === 'nl' ? 'Leads analyseren...' : 'Analyzing leads...'}</h3></div>
            )}
            {(Array.isArray(messages) ? messages : []).map((msg, i) => (
              <div key={i} className={`compass-msg compass-msg-${msg.role}`}>
                {msg.role === 'assistant' ? <div className="compass-msg-content markdown-content"><ReactMarkdown>{msg.content}</ReactMarkdown></div> : <div className="compass-msg-content">{msg.content}</div>}
              </div>
            ))}
            {loading && messages.length > 0 && <div className="compass-msg compass-msg-assistant"><div className="compass-msg-content compass-typing"><Loader className="spin" size={16} /> {lang === 'nl' ? 'Bezig...' : 'Working...'}</div></div>}
            <div ref={chatEndRef} />
          </div>
          <form className="compass-chat-input" onSubmit={(e) => { e.preventDefault(); sendChat(input); }}>
            <input type="text" value={input} onChange={(e) => setInput(e.target.value)} placeholder={tp.chatPlaceholder} disabled={loading} className="form-input" data-testid="sales-chat-input" />
            <button type="submit" disabled={loading || !((input) == null ? '' : String(input)).trim()} className="btn-primary compass-send-btn"><Send size={16} /></button>
          </form>
        </div>
      </main>
    </div>
  );
};

export default SalesAssistant;
